import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, CheckCircle2, ExternalLink, EyeOff, Loader2, Pencil, Plus, Star, StarOff, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const EMPTY = { display_name: '', review_text: '', context: '', source_url: '', source_label: '', review_date: '', featured: false, status: 'approved', admin_note: '', reply_text: '' };
const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-900 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  unpublished: 'bg-slate-100 text-slate-700 border-slate-300',
  rejected: 'bg-red-50 text-red-800 border-red-200',
};

export default function AdminReviews() {
  const [data, setData] = useState({ reviews: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [editing, setEditing] = useState(null); // review | 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/reviews').then((r) => setData(r.data || { reviews: [], counts: {} }))
      .catch(() => toast.error('Could not load reviews')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = data.reviews.filter((r) => (tab === 'all' ? true : tab === 'featured' ? (r.featured && r.status === 'approved') : r.status === tab));
  const featuredOrdered = data.reviews.filter((r) => r.featured && r.status === 'approved').sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };
  const approve = (r) => act(() => api.post(`/admin/reviews/${r.id}/approve`), 'Review approved and published');
  const unpublish = (r) => act(() => api.post(`/admin/reviews/${r.id}/unpublish`), 'Review unpublished (kept on file)');
  const toggleFeature = (r) => act(() => api.post(`/admin/reviews/${r.id}/feature`, { featured: !r.featured }), r.featured ? 'Removed from featured' : 'Featured on the homepage');
  const move = (r, dir) => {
    const ids = featuredOrdered.map((x) => x.id);
    const i = ids.indexOf(r.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.post('/admin/reviews/reorder', { ids }), 'Featured order updated');
  };
  const remove = () => act(async () => { await api.delete(`/admin/reviews/${deleting.id}`); setDeleting(null); }, 'Review deleted');

  const openEdit = (r) => {
    if (r === 'new') setForm(EMPTY);
    else setForm({ display_name: r.display_name || '', review_text: r.review_text || '', context: r.context || '', source_url: r.source_url || '', source_label: r.source_label || '', review_date: r.review_date || '', featured: !!r.featured, status: r.status || 'pending', admin_note: r.admin_note || '', reply_text: r.reply_text || '' });
    setEditing(r);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') { await api.post('/admin/reviews', form); toast.success('Review added'); }
      else { await api.put(`/admin/reviews/${editing.id}`, form); toast.success('Review saved'); }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const c = data.counts || {};
  return (
    <div className="space-y-5" data-testid="admin-reviews-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-[#1a2744]">Reviews and Testimonials</h1>
          <p className="text-sm text-slate-500 mt-1">Approve website submissions, add reviews that already exist elsewhere, feature the best ones on the homepage, and fix display details. Edits are for spelling and display only: never change what the reviewer meant.</p>
        </div>
        <Button onClick={() => openEdit('new')} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="admin-reviews-add-button"><Plus className="h-4 w-4 mr-1.5" /> Add Existing Review</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-reviews-counts">
        {[['pending', 'Waiting for approval'], ['approved', 'Published'], ['featured', 'Featured on homepage'], ['unpublished', 'Unpublished']].map(([k, label]) => (
          <Card key={k} className={tab === k ? 'border-[#1a2744]' : ''}>
            <CardContent className="p-4">
              <p className="text-2xl font-black text-[#1a2744]" data-testid={`admin-reviews-count-${k}`}>{c[k] ?? 0}</p>
              <p className="text-xs text-slate-500 font-bold">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto" data-testid="admin-reviews-tabs">
          {['pending', 'approved', 'featured', 'unpublished', 'rejected', 'all'].map((t) => (
            <TabsTrigger key={t} value={t} className="font-bold capitalize" data-testid={`admin-reviews-tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading reviews...</div>
      ) : list.length === 0 ? (
        <Card data-testid="admin-reviews-empty"><CardContent className="p-8 text-center text-slate-500">No reviews in this view.</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="admin-reviews-list">
          {(tab === 'featured' ? featuredOrdered : list).map((r, idx) => (
            <Card key={r.id} data-testid={`admin-review-row-${r.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={`font-bold ${STATUS_STYLE[r.status] || ''}`} data-testid={`admin-review-status-${r.id}`}>{r.status}</Badge>
                  {r.featured && r.status === 'approved' && <Badge className="bg-[#C9A84C] text-[#1a2744] font-bold border-0"><Star className="h-3 w-3 mr-1" /> Featured{tab === 'featured' ? ` #${idx + 1}` : ''}</Badge>}
                  <Badge variant="outline" className="text-slate-600">{r.origin === 'admin' ? 'Added by admin' : 'Website submission'}</Badge>
                  <span className="text-xs text-slate-400 ml-auto">{(r.created_at || '').slice(0, 10)}</span>
                </div>
                <p className="font-serif font-bold text-[#1a2744] mt-3" data-testid={`admin-review-name-${r.id}`}>{r.display_name}{r.context ? <span className="text-xs text-slate-500 font-normal ml-2">{r.context}</span> : null}</p>
                <p className="text-sm text-slate-700 mt-1.5 leading-relaxed whitespace-pre-line" data-testid={`admin-review-text-${r.id}`}>{r.review_text}</p>
                {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#1a2744] underline mt-2">Original post{r.source_label ? ` (${r.source_label})` : ''} <ExternalLink className="h-3 w-3" /></a>}
                {r.reply_text && <p className="text-xs text-[#1a2744] mt-2 bg-[#f3f7ff] border border-[#c9d8f0] rounded p-2" data-testid={`admin-review-reply-${r.id}`}><strong>Steve's public reply:</strong> {r.reply_text}</p>}
                {r.admin_note && <p className="text-xs text-slate-500 mt-2 bg-slate-50 border border-slate-200 rounded p-2">Private note: {r.admin_note}</p>}
                <div className="flex flex-wrap gap-2 mt-4">
                  {r.status !== 'approved' && <Button size="sm" onClick={() => approve(r)} className="h-9 bg-emerald-700 hover:bg-emerald-800 text-white font-bold btn-press" data-testid={`admin-review-approve-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve &amp; publish</Button>}
                  {r.status === 'approved' && <Button size="sm" variant="outline" onClick={() => unpublish(r)} className="h-9 font-bold btn-press" data-testid={`admin-review-unpublish-${r.id}`}><EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish</Button>}
                  {r.status === 'approved' && <Button size="sm" variant="outline" onClick={() => toggleFeature(r)} className="h-9 font-bold btn-press" data-testid={`admin-review-feature-${r.id}`}>{r.featured ? <><StarOff className="h-3.5 w-3.5 mr-1" /> Unfeature</> : <><Star className="h-3.5 w-3.5 mr-1" /> Feature</>}</Button>}
                  {tab === 'featured' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => move(r, -1)} disabled={idx === 0} className="h-9 btn-press" aria-label="Move up" data-testid={`admin-review-up-${r.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => move(r, 1)} disabled={idx === featuredOrdered.length - 1} className="h-9 btn-press" aria-label="Move down" data-testid={`admin-review-down-${r.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="h-9 font-bold btn-press" data-testid={`admin-review-edit-${r.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit details</Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleting(r)} className="h-9 text-red-600 border-red-200 hover:bg-red-50 btn-press ml-auto" data-testid={`admin-review-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-review-dialog">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Add an existing review' : 'Edit display details'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editing !== 'new' && editing?.origin === 'website' && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="admin-review-meaning-warning">This review was written by a visitor. Fix spelling or trim for length only. Do not change what the reviewer meant. The original wording is kept on file.</p>
            )}
            <div>
              <Label htmlFor="rv-name">Display name</Label>
              <Input id="rv-name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} className="mt-1" data-testid="admin-review-name-input" />
            </div>
            <div>
              <Label htmlFor="rv-context">Context line (optional, public)</Label>
              <Input id="rv-context" value={form.context} onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))} placeholder="For example: Worked with Steve at Chairs and More" className="mt-1" data-testid="admin-review-context-input" />
            </div>
            <div>
              <Label htmlFor="rv-text">Review text</Label>
              <Textarea id="rv-text" rows={6} value={form.review_text} onChange={(e) => setForm((f) => ({ ...f, review_text: e.target.value }))} className="mt-1" data-testid="admin-review-text-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rv-source">Original post link (optional)</Label>
                <Input id="rv-source" value={form.source_url} onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))} placeholder="https://www.facebook.com/..." className="mt-1" data-testid="admin-review-source-input" />
              </div>
              <div>
                <Label htmlFor="rv-source-label">Source label (optional)</Label>
                <Input id="rv-source-label" value={form.source_label} onChange={(e) => setForm((f) => ({ ...f, source_label: e.target.value }))} placeholder="Facebook, Google, LinkedIn" className="mt-1" data-testid="admin-review-source-label-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rv-date">Review date (optional)</Label>
                <Input id="rv-date" type="date" value={form.review_date} onChange={(e) => setForm((f) => ({ ...f, review_date: e.target.value }))} className="mt-1" data-testid="admin-review-date-input" />
              </div>
              <div>
                <Label htmlFor="rv-status">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger id="rv-status" className="mt-1 h-10 capitalize" data-testid="admin-review-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['pending', 'approved', 'unpublished', 'rejected'].map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`admin-review-status-option-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Featured on homepage</p>
                <p className="text-xs text-slate-500">Only approved reviews are shown. Arrange the order in the Featured tab.</p>
              </div>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="admin-review-featured-switch" />
            </div>
            <div>
              <Label htmlFor="rv-reply">Steve's reply (public, shown under the review once approved)</Label>
              <Textarea id="rv-reply" rows={2} maxLength={600} value={form.reply_text} onChange={(e) => setForm((f) => ({ ...f, reply_text: e.target.value }))} placeholder="A short thank-you from Steve. Leave empty for no reply." className="mt-1" data-testid="admin-review-reply-input" />
              <p className="text-[11px] text-slate-500 mt-1 mb-3">{(form.reply_text || '').length} / 600. Keep it personal and brief; residents see it exactly as written.</p>
              <Label htmlFor="rv-note">Private admin note (never public)</Label>
              <Textarea id="rv-note" rows={2} value={form.admin_note} onChange={(e) => setForm((f) => ({ ...f, admin_note: e.target.value }))} placeholder="Where this came from, why an edit was made..." className="mt-1" data-testid="admin-review-note-input" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="h-11 font-bold btn-press" onClick={() => setEditing(null)} data-testid="admin-review-cancel-button">Cancel</Button>
              <Button className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" onClick={save} disabled={saving || !form.display_name.trim() || !form.review_text.trim()} data-testid="admin-review-save-button">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <AlertDialogContent data-testid="admin-review-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review permanently?</AlertDialogTitle>
            <AlertDialogDescription>{deleting ? `The review from "${deleting.display_name}" will be removed for good. To hide it while keeping it on file, use Unpublish instead.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-review-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="admin-review-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
